import { ORPCError } from '@orpc/client'
import type { InvitationPreview } from '@sirius/contracts/admin'

/**
 * What the invitation's page shows:
 * - `loading` until the invitation and the session are read
 * - `notFound` for a link without a token, an unknown invitation or a token
 *   that is not its own (the API cannot tell them apart), `error` when the
 *   read failed otherwise
 * - the state of an invitation that can no longer be accepted
 * - for an open one: `signUp` (no account for its email yet) or `signIn`
 *   when signed out, `accept` when signed in with its email, `wrongAccount`
 *   when signed in as someone else
 */
export type AcceptInvitationView =
  | { kind: 'loading' }
  | { kind: 'notFound' }
  | { kind: 'error'; error: unknown }
  | {
      kind: 'expired' | 'canceled' | 'rejected' | 'accepted'
      preview: InvitationPreview
    }
  | { kind: 'signUp' | 'signIn' | 'accept'; preview: InvitationPreview }
  | { kind: 'wrongAccount'; preview: InvitationPreview; signedInAs: string }

/** The signed-in user (null: signed out, undefined: not read yet). */
type SessionUser = { email: string } | null | undefined

export function acceptInvitationView({
  token,
  preview,
  previewError,
  sessionUser,
}: {
  /** the link's `?token=` */
  token: string | null
  preview: InvitationPreview | undefined
  previewError: unknown
  sessionUser: SessionUser
}): AcceptInvitationView {
  if (token === null || token === '') {
    return { kind: 'notFound' }
  }
  if (previewError !== null && previewError !== undefined) {
    // a cut-short id fails the input's check (BAD_REQUEST)
    return previewError instanceof ORPCError &&
      (previewError.code === 'NOT_FOUND' || previewError.code === 'BAD_REQUEST')
      ? { kind: 'notFound' }
      : { kind: 'error', error: previewError }
  }
  if (preview === undefined) {
    return { kind: 'loading' }
  }
  if (preview.status !== 'pending') {
    return { kind: preview.status, preview }
  }
  if (sessionUser === undefined) {
    return { kind: 'loading' }
  }
  if (sessionUser === null) {
    return { kind: preview.hasAccount ? 'signIn' : 'signUp', preview }
  }
  return sessionUser.email.toLowerCase() === preview.email.toLowerCase()
    ? { kind: 'accept', preview }
    : { kind: 'wrongAccount', preview, signedInAs: sessionUser.email }
}
